import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import posthog from 'posthog-js';
import ProgressBar from './ProgressBar';
import WelcomeStep from './WelcomeStep';
import MedicalCardStep from './MedicalCardStep';
import VehicleStep from './VehicleStep';
import PlanStep from './PlanStep';
import VerifyIdentityStep from './VerifyIdentityStep';
import CompleteStep from './CompleteStep';
import API_URL from '../../config';
import { showInfo } from '../../utils/toast';
import {
  readUserFromStorage,
  isFreeSubscription,
  mergeSubscriptionTier,
  notifySubscriptionUpdated,
} from '../../utils/subscription';
import '../../App.css';

/** 0 = welcome (no progress), 1–3 = input steps, 4 = identity (paid only), 5 = completion */
const STEP_WELCOME = 0;
const STEP_PLAN = 3;
const STEP_VERIFY_IDENTITY = 4;
const STEP_COMPLETE = 5;
const INPUT_STEP_COUNT = 3;
const CHECKOUT_POLL_MS = 2000;
const CHECKOUT_POLL_TIMEOUT_MS = 30000;

function checkoutQueryValue() {
  return new URLSearchParams(window.location.search).get('checkout');
}

function clearCheckoutQuery() {
  window.history.replaceState({}, document.title, window.location.pathname);
}

function isFreeAccount() {
  return isFreeSubscription(readUserFromStorage());
}

function isPaidAccount() {
  return !isFreeAccount();
}

/** Identity verification is Owner-Operator / Fleet-Pro only. */
function skipsIdentityStep() {
  return isFreeAccount();
}

const Onboarding = () => {
  const navigate = useNavigate();
  const checkoutReturnHandledRef = useRef(false);
  const [currentStep, setCurrentStep] = useState(() =>
    checkoutQueryValue() === 'canceled' ? STEP_PLAN : STEP_WELCOME
  );
  const [checkoutPhase, setCheckoutPhase] = useState(() =>
    checkoutQueryValue() === 'success' ? 'confirming' : null
  );
  const [onboardingData, setOnboardingData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    cdlNumber: '',
    cdlState: '',
    dotNumber: '',
    medicalCardUploaded: false,
    vehicleAdded: false,
    selectedPlan: 'free',
    progress: { step: 1, completed: [] },
  });
  const [userId, setUserId] = useState(null);

  // Flow: Welcome (unnumbered) → Medical → Vehicle → Plan → Complete

  useEffect(() => {
    // Get user ID from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setUserId(user.id);
        setOnboardingData((prev) => ({
          ...prev,
          firstName: user.firstName || prev.firstName,
          lastName: user.lastName || prev.lastName,
          phone: user.phone || prev.phone,
          cdlNumber: user.cdlNumber || prev.cdlNumber,
          cdlState: user.cdlState || prev.cdlState,
          dotNumber: user.dotNumber || prev.dotNumber,
        }));
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }

    // Check if user is already logged in
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }
  }, [navigate]);

  useEffect(() => {
    if (checkoutQueryValue()) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    let cancelled = false;
    (async () => {
      try {
        const profile = await axios.get(`${API_URL}/api/auth/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled || !profile.data) return;
        localStorage.setItem(
          'user',
          JSON.stringify(mergeSubscriptionTier(profile.data)),
        );
        notifySubscriptionUpdated();
        if (isPaidAccount() && profile.data.onboardingCompleted !== true) {
          setCurrentStep(STEP_COMPLETE);
        }
      } catch {
        /* keep current onboarding start */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const nextStepFrom = (step) => {
    if (step >= STEP_COMPLETE) return step;
    const next = step + 1;
    if (next === 2 && isFreeAccount()) return STEP_PLAN;
    if (next === STEP_PLAN && isPaidAccount()) {
      return skipsIdentityStep() ? STEP_COMPLETE : STEP_VERIFY_IDENTITY;
    }
    if (next === STEP_VERIFY_IDENTITY && skipsIdentityStep()) return STEP_COMPLETE;
    return next;
  };

  useEffect(() => {
    const checkout = checkoutQueryValue();
    if (checkout === 'canceled') {
      if (checkoutReturnHandledRef.current) return;
      checkoutReturnHandledRef.current = true;
      setCurrentStep(STEP_PLAN);
      showInfo('Checkout was canceled.');
      clearCheckoutQuery();
      return;
    }
    if (checkout !== 'success') return;
    if (checkoutReturnHandledRef.current) return;
    checkoutReturnHandledRef.current = true;

    const token = localStorage.getItem('token');
    const deadline = Date.now() + CHECKOUT_POLL_TIMEOUT_MS;

    const finishPaid = async () => {
      const profile = await axios.get(`${API_URL}/api/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (profile.data) {
        localStorage.setItem(
          'user',
          JSON.stringify(mergeSubscriptionTier(profile.data)),
        );
        notifySubscriptionUpdated();
      }
      setCurrentStep(nextStepFrom(STEP_PLAN));
      clearCheckoutQuery();
      setCheckoutPhase(null);
    };

    const tick = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/subscriptions/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const tier = String(data?.tier || 'free').toLowerCase().trim();
        if (tier !== 'free') {
          await finishPaid();
          return;
        }
      } catch {
        /* keep polling until the deadline */
      }
      if (Date.now() >= deadline) {
        setCheckoutPhase('activating');
        return;
      }
      window.setTimeout(tick, CHECKOUT_POLL_MS);
    };

    tick();
  }, []);

  const prevStepFrom = (step) => {
    if (step <= STEP_WELCOME) return step;
    if (step === STEP_PLAN && isFreeAccount()) return 1;
    const prev = step - 1;
    if (prev === STEP_PLAN && isPaidAccount()) return 2;
    if (prev === STEP_VERIFY_IDENTITY && skipsIdentityStep()) return STEP_PLAN;
    return prev;
  };

  const handleNext = () => {
    setCurrentStep((s) => nextStepFrom(s));
  };

  const handleBack = () => {
    setCurrentStep((s) => prevStepFrom(s));
  };

  const handleSkip = () => {
    handleNext();
  };

  const handleContinue = (stepData) => {
    setOnboardingData((prev) => ({
      ...prev,
      ...stepData,
    }));
    setCurrentStep((s) => nextStepFrom(s));
  };

  const handleSignOut = () => {
    posthog.reset();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
    window.location.reload();
  };

  const signOutLink = (
    <button type="button" className="onboarding-sign-out" onClick={handleSignOut}>
      Sign out
    </button>
  );

  const renderStep = () => {
    switch (currentStep) {
      case STEP_WELCOME:
        return <WelcomeStep onNext={handleNext} />;
      case 1:
        return (
          <MedicalCardStep
            onNext={handleNext}
            onBack={handleBack}
            onSkip={handleSkip}
            onboardingData={onboardingData}
            setOnboardingData={setOnboardingData}
            userId={userId}
          />
        );
      case 2:
        return (
          <VehicleStep
            onNext={handleNext}
            onBack={handleBack}
            onSkip={handleSkip}
            onboardingData={onboardingData}
            setOnboardingData={setOnboardingData}
            userId={userId}
          />
        );
      case STEP_PLAN:
        return (
          <PlanStep
            onContinue={handleContinue}
            onBack={handleBack}
            onboardingData={onboardingData}
            setOnboardingData={setOnboardingData}
          />
        );
      case STEP_VERIFY_IDENTITY:
        return <VerifyIdentityStep onContinue={handleNext} onBack={handleBack} />;
      case STEP_COMPLETE:
        return (
          <CompleteStep
            onboardingData={onboardingData}
            userId={userId}
          />
        );
      default:
        return <WelcomeStep onNext={handleNext} />;
    }
  };

  if (checkoutPhase === 'confirming') {
    return (
      <div className="onboarding-container" data-onboarding-flow="1">
        <div className="onboarding-card">
          <div className="dashboard-loading">
            <div className="loading-spinner"></div>
            <p>Confirming your payment...</p>
          </div>
        </div>
      </div>
    );
  }

  if (checkoutPhase === 'activating') {
    return (
      <div className="onboarding-container" data-onboarding-flow="1">
        <div className="onboarding-card">
          {signOutLink}
          <div className="dashboard-loading">
            <p>Your payment went through and your plan is still activating</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                clearCheckoutQuery();
                setCheckoutPhase(null);
                setCurrentStep(STEP_COMPLETE);
              }}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="onboarding-container" data-onboarding-flow="1">
        <div className="onboarding-card">
          <div className="dashboard-loading">
            <div className="loading-spinner"></div>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  const showProgress =
    currentStep >= 1 && currentStep <= INPUT_STEP_COUNT;
  const freeAccount = isFreeAccount();
  const progressTotalSteps = freeAccount ? 2 : INPUT_STEP_COUNT;
  const progressCurrentStep =
    freeAccount && currentStep === STEP_PLAN ? 2 : currentStep;

  return (
    <div className="onboarding-container" data-onboarding-flow="1">
      <div className="onboarding-card">
        {signOutLink}
        {showProgress && (
          <ProgressBar
            currentStep={progressCurrentStep}
            totalSteps={progressTotalSteps}
          />
        )}
        {renderStep()}
      </div>
    </div>
  );
};

export default Onboarding;
